import { CardData } from "../../types/types";
import { Card, CardRank, CardSuit } from "../card/Card";
import { LoaderFileKeys } from "../constant/AppConstants";
import { ChangedItemData, LiveDataChangeEvents, OptionsDataManager } from "./OptionsDataManager";
import { AppLocalStorage } from "./AppLocalStorage";
import { OptionsItemKeys } from "./LayoutDataManager";


export type CardRankProps = {
    valueOther: number;
    valueTrump: number;
    /**
     * NO TRUMPS = Ace is worth 19, rest cards are worth a in 'other' suit
     */
    valueNoTrumps: number;
    /**
     * ALL TRUMPS = different card values from original belote/NT
     */
    valueAllTrumps: number;
    valueNoTrumps130: number;
    valueAllTrumps258: number;
    /**
     * Order by which to sort cards in 'other than trump' group; 
     * smaller is lower (comes first from right/default)
     */
    rankOrderOther: number;
    /**
     * Same as @see rankOrderOther, but for Trumps.
     */
    rankOrderTrump: number;
    rankOrderNoTrumps: number;
    rankOrderAllTrumps: number;
    rankOrderNoTrumps130: number;
    rankOrderAllTrumps258: number;
}

/**
 * is an invis layer?
 */
export class CardsManager {
    public static noAvoidSameCardColorNeighbourSuitOrder: CardSuit[] = [
        CardSuit.HEARTS,     // symbol: ♥
        CardSuit.DIAMONDS,   // symbol: ♦ 
        CardSuit.CLUBS,      // symbol: ♣
        CardSuit.SPADES,     // symbol: ♠
    ];
    public static avoidSameCardColorNeighbourSuitOrder: CardSuit[] = [
        CardSuit.HEARTS,     // symbol: ♥
        CardSuit.CLUBS,      // symbol: ♣
        CardSuit.DIAMONDS,   // symbol: ♦ 
        CardSuit.SPADES,     // symbol: ♠
    ];
    // --------------------
    // TRUMP: J(20),  9(14), A(11), 10(10), K(4), Q(3), 8(0), 7(0)
    // OTHER: A(11), 10(10), K(4) ,  Q(3) , J(2), 9(0), 8(0), 7(0)
    // --------------------
    // COINCHE OVERRIDE:
    // AT: J(13),  9(9) , A(6), 10(5), K(3), Q(2), 8(0), 7(0)
    // NT: A(19), 10(10), K(4),  Q(3), J(2), 9(0), 8(0), 7(0)
    public static cardRankToCardProps: CardRankProps[] = [
        // SEVEN,  // 7    index: 0
        {
            rankOrderOther: 0, valueOther: 0,
            rankOrderTrump: 0, valueTrump: 0,
            rankOrderNoTrumps: 0, valueNoTrumps: 0,
            rankOrderAllTrumps: 0, valueAllTrumps: 0,
            rankOrderNoTrumps130: 0, valueNoTrumps130: 0,
            rankOrderAllTrumps258: 0, valueAllTrumps258: 0,
        },
        // EIGHT,  // 8    index: 1
        {
            rankOrderOther: 1, valueOther: 0,
            rankOrderTrump: 1, valueTrump: 0,
            rankOrderNoTrumps: 1, valueNoTrumps: 0,
            rankOrderAllTrumps: 1, valueAllTrumps: 0,
            rankOrderNoTrumps130: 1, valueNoTrumps130: 0,
            rankOrderAllTrumps258: 1, valueAllTrumps258: 0,
        },
        // NINE,   // 9    index: 2
        {
            rankOrderOther: 2, valueOther: 0,
            rankOrderTrump: 6, valueTrump: 14,
            rankOrderNoTrumps: 2, valueNoTrumps: 0,
            rankOrderAllTrumps: 6, valueAllTrumps: 9,
            rankOrderNoTrumps130: 2, valueNoTrumps130: 0,
            rankOrderAllTrumps258: 6, valueAllTrumps258: 14,
        },
        // TEN,    // 10   index: 3
        {
            rankOrderOther: 6, valueOther: 10,
            rankOrderTrump: 4, valueTrump: 10,
            rankOrderNoTrumps: 6, valueNoTrumps: 10,
            rankOrderAllTrumps: 4, valueAllTrumps: 5,
            rankOrderNoTrumps130: 6, valueNoTrumps130: 10,
            rankOrderAllTrumps258: 4, valueAllTrumps258: 10,
        },
        // JACK,   // J/V(Vale=FR)    index: 4
        {
            rankOrderOther: 3, valueOther: 2,
            rankOrderTrump: 7, valueTrump: 20,
            rankOrderNoTrumps: 3, valueNoTrumps: 2,
            rankOrderAllTrumps: 7, valueAllTrumps: 13,
            rankOrderNoTrumps130: 3, valueNoTrumps130: 2,
            rankOrderAllTrumps258: 7, valueAllTrumps258: 20,
        },
        // QUEEN,  // Q    index: 5
        {
            rankOrderOther: 4, valueOther: 3,
            rankOrderTrump: 2, valueTrump: 3,
            rankOrderNoTrumps: 4, valueNoTrumps: 3,
            rankOrderAllTrumps: 2, valueAllTrumps: 2,
            rankOrderNoTrumps130: 4, valueNoTrumps130: 3,
            rankOrderAllTrumps258: 2, valueAllTrumps258: 3,
        },
        // KING,   // K    index: 6
        {
            rankOrderOther: 5, valueOther: 4,
            rankOrderTrump: 3, valueTrump: 4,
            rankOrderNoTrumps: 5, valueNoTrumps: 4,
            rankOrderAllTrumps: 3, valueAllTrumps: 3,
            rankOrderNoTrumps130: 5, valueNoTrumps130: 4,
            rankOrderAllTrumps258: 3, valueAllTrumps258: 4,
        },
        // ACE,    // A    index: 7
        {
            rankOrderOther: 7, valueOther: 11,
            rankOrderTrump: 5, valueTrump: 11,
            rankOrderNoTrumps: 7, valueNoTrumps: 19,
            rankOrderAllTrumps: 5, valueAllTrumps: 6,
            rankOrderNoTrumps130: 7, valueNoTrumps130: 11,
            rankOrderAllTrumps258: 5, valueAllTrumps258: 11,
        },
    ];

    private static scene: Phaser.Scene;
    /**
     * Pool cards are stored in here;
     * All pool cards are playable cards, but not all playable cards are always inside pool (they can be on table)
     */
    public static cardsPool: Phaser.GameObjects.Container;
    private static cardBackFrameNameCached: string = undefined;

    /**
     * reference to all playable cards (not declaration or animation cards..),
     * no matter where they are (in pool, on table..)
     * - unsorted (index is deceptive/shuffled)
     */
    private static playableCards: Card[];

    /**
     * reference to all playable cards as CardData[]s @see playableCards
     * - unsorted (index is deceptive/shuffled)
     */
    private static playableCardDatas: CardData[];


    public static Init(scene: Phaser.Scene): void {
        this.scene = scene;

        // const layerConfig:Phaser.Types.GameObjects.Sprite.SpriteConfig = 
        this.playableCards = [];
        this.playableCardDatas = [];
        this.cardsPool = this.scene.make.container({}, false); // config, addToScene?
        this.cardsPool.name = "cardsPool";
    }

    public static Build(): void {
        this.CreateCardsFromCardAtlasJSON(LoaderFileKeys.cardsDeck);

        // listen to data model background change
        OptionsDataManager.GetEventEmitter().on(LiveDataChangeEvents.CARD_BACK_CHANGE, this.OnCardBackDataChange, this);
    }

    public static Shuffle(): void {
        // modifies in place;
        // WARNING: make sure to only call this once all cards are back in pool
        Phaser.Utils.Array.Shuffle(this.playableCards);
    }

    /**
     * NOTE: This only happens once in Build
     * @param cardsDeckKey 
     */
    public static CreateCardsFromCardAtlasJSON(cardsDeckKey: string): void {
        const cardsDeckFrames: string[] = this.scene.textures.get(cardsDeckKey).getFrameNames();
        // console.log(cardsDeckFrames);
        // let cardId: number = 0;
        for (let i: number = 0; i < cardsDeckFrames.length; i++) {
            const cardFrame: string = cardsDeckFrames[i];
            // if not the 'small' variation
            if (cardFrame.indexOf("card-") != -1) {
                const lastDashIndex = cardFrame.lastIndexOf("-");
                const lastDotIndex = cardFrame.lastIndexOf(".");
                const rank: CardRank = parseInt(cardFrame.substring(lastDashIndex + 1, lastDotIndex));
                const firstDashIndex = cardFrame.indexOf("-");
                // const secondDashIndex = cardFrame.indexOf("-", firstDashIndex + 1);
                const suit: CardSuit = parseInt(cardFrame.substring(firstDashIndex + 1, lastDashIndex));

                const cardBackFrame: string = this.GetCardBackFrameName();
                const frontFacing: boolean = true;
                const card: Card = new Card(this.scene);
                card.Init(
                    { texture: LoaderFileKeys.cardsDeck, frame: cardFrame },  // card front
                    { texture: LoaderFileKeys.cardsBacks, frame: cardBackFrame }, // card back
                    { texture: LoaderFileKeys.ui, frame: "card_shadow.png" },   // shadow
                    null,   // faded over
                    rank,
                    suit,
                    frontFacing
                );
                // card.id = cardId++;
                card.Build();
                this.cardsPool.add(card);
                this.playableCards.push(card);
                this.playableCardDatas.push({ rank: rank, suit: suit });
            }
        }
    }

    public static GetPlayableCards(): Card[] {
        return this.playableCards;
    }

    public static GetPlayableCardDatas(): CardData[] {
        return this.playableCardDatas;
    }

    /**
     * Pool remaining playable cards that are not in pool yet
     * - to ensure all cards are in pool for Table Reset, pool all cards
     *   using correct means (locally in components) and pool the rest
     *   (unreferenced cards flying/being animated etc somewhere)
     *   using this function.
     */
    public static PoolPlayableCards(): void {
        this.playableCards.forEach(card => {
            // console.log(card.parentContainer.name);
            if (card.parentContainer && card.parentContainer.name != "cardsPool") {
                this.DropCard(card);
            }
        });
    }

    /**
     * Return random card, or existing card by specific suit/rank or null id card not in pool
     * @param suit 
     * @param rank 
     * @returns 
     */
    public static PickCard(
        suit: CardSuit = undefined,
        rank: CardRank = undefined,
        frontFacing: boolean = undefined
    ): Card | null {
        let card: Card;
        if (suit == undefined && rank == undefined) {
            card = this.cardsPool.removeLast() as Card; // returns null if cont. empty
            return card;
        }
        for (let i: number = 0; i < this.cardsPool.length; i++) {
            card = this.cardsPool.getAt(i) as Card;
            if (card.suit == suit && card.rank == rank) {
                // modifies arraym returns deleted elements
                // const returnCard:Card = this.cards.splice(i, 1)[0];
                this.cardsPool.removeAt(i);
                if (frontFacing != undefined) {
                    card.SetFrontFacing(frontFacing);
                }
                return card;
            }
        }
        return null;
    }

    public static GetLength(): number {
        return this.cardsPool.length;
    }

    public static GetCards(): Card[] {
        return this.cardsPool.list as Card[];
    }

    /**
     * 
     * @param count 
     * @param frontFacing Sets returned card(s) to be frontFacing (true) or backFacing (false) OR if left undefined, will SKIP SETTING frontFacing (old facing from pool will be preserved)
     */
    public static PickCards(count: number, frontFacing?: boolean): Card[];
    public static PickCards(pickCards: Card[], frontFacing?: boolean): Card[];
    public static PickCards(pickCards: CardData[], frontFacing?: boolean): Card[];
    public static PickCards(
        a: number | Card[] | CardData[],
        frontFacing: boolean = undefined
    ): Card[] {
        if (typeof a === "number") {
            // if(Number.isInteger(a)) {
            const returnCards: Card[] = this.cardsPool.removeLastCount(a) as Card[];
            if (frontFacing != undefined) {
                returnCards.forEach(card => {
                    card.SetFrontFacing(frontFacing);
                })
            }
            return returnCards;
        }
        else {
            if (a.length && (a[0] instanceof Card)) {
                for (let i: number = a.length - 1; i >= 0; i--) {
                    const pickCard: Card = a[i] as Card;
                    this.cardsPool.remove(pickCard);
                    if (frontFacing != undefined) {
                        pickCard.SetFrontFacing(frontFacing);
                    }
                }
                return a as Card[];
            }
            else {
                // if (a.length && (typeof a[0] === "object")) {
                // if (a.length && (a[0] instanceof CardData)) { // ERROR: CardData is a Type!
                if (a.length && (a[0] instanceof Object)) { // read: Object->CardData
                    // const returnCards: Card[] = [];
                    // (a as CardData[]).forEach((cardData:CardData)=> {
                    //     // using 'each' for safe removal of items
                    //     this.cardsPool.each((card:Card)=>{
                    //         if (card.rank == cardData.rank && 
                    //         card.suit == cardData.suit) {
                    //             this.cardsPool.remove(card);
                    //             returnCards.push(card);
                    //         }
                    //     })
                    // })
                    const returnCards: Card[] = (a as CardData[]).map((cardData: CardData, i: number) => {
                        return CardsManager.PickCard(cardData.suit, cardData.rank, frontFacing);
                    });
                    return returnCards;
                }
            }
        }
        return [];
    }

    /**
     * Drop existing card back into pool
     * @param card 
     */
    public static DropCard(card: Card, frontFacing: boolean = undefined): void {
        // frontFacing 
        if (frontFacing != undefined) {
            card.SetFrontFacing(frontFacing);
            // console.log("set card",card.log(),"to be frontfacing:",frontFacing)
        }
        // reset card to default state
        card.Reset();
        // 
        this.cardsPool.add(card);
    }

    public static DropCards(newCards: Card[], frontFacing: boolean = undefined): void {
        while (newCards.length) {
            this.DropCard(newCards.pop());
        }
    }

    /**
     * Utility;
     * Supply cdCards:CardData[] and cCards:Card[] and returns filteredCards:CardData[] matching c data
     */
    public static GetCardsByCardData(cdCards: CardData[], cCards: Card[]): Card[] {
        const result: Card[] = [];
        let cCard: Card;
        let cdCard: CardData;
        for (let i: number = 0; i < cCards.length; i++) {
            cCard = cCards[i];
            for (let j: number = 0; j < cdCards.length; j++) {
                cdCard = cdCards[j];
                if ((cdCard.rank == cCard.rank) && (cdCard.suit == cCard.suit)) {
                    result.push(cCard);
                    break;
                }
            }
        }
        // console.log(result.length);

        return result;
    }

    public static GetCardFrontFrameName(suit: number, rank: number): string {
        return `card-${suit}-${rank}.png`;
    }

    public static GetCardSmallFrontFrameName(suit: number, rank: number): string {
        return `cardSmall-${suit}-${rank}.png`;
    }

    public static GetCardBackFrameName(): string {
        if (this.cardBackFrameNameCached == null) {
            // const cardBackIndex: number = AppLocalStorage.GetCardsBackIndex();
            const cardBackIndex: number = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.cardBack).value as number;

            this.cardBackFrameNameCached = `cardBack-${cardBackIndex}.png`;
        }
        return this.cardBackFrameNameCached;
    }

    private static OnCardBackDataChange(itemData: ChangedItemData): void {
        // console.log("OnCardBackDataChange", itemData.live.value);
        const cardBackIndex: number = itemData.live.value as number;
        const cardBackFrame: string = itemData.theme.items[cardBackIndex].source.textureFrame;

        // set view
        this.playableCards.forEach((playableCard: Card) => {
            playableCard.SetBackTextureData({
                texture: LoaderFileKeys.cardsBacks,
                frame: cardBackFrame
            }, true); // UpdateView?
        });
    }
}